<template id="drilldown-report-page">
  <section>
    <div class="container">
      <div class="card shadow-sm border-0">
        <div class="card-header bg-primary text-white py-2 d-flex justify-content-center align-items-center">
          <strong><i class="fas fa-file-upload mr-2"></i>File Upload Details</strong>
        </div>

        <div class="card-body p-3">
          <!-- Grid layout: 3 fields per row -->
          <div class="form-row">
            <!-- File Identifier -->
            <div
                v-for="(item, idx) in screenletListboxDim"
                :key="idx"
                class="form-group col-md-3 mb-3"
            >
              <label class="col-form-label col-form-label-sm font-weight-bold">
                {{ SLlistboxDimBizName[idx] }}
              </label>
              <div v-if="screenletListboxType === 'anyWhichWay'">
                <select
                    v-model="fileIdentifier[item.name]"
                    @change="updateFileType"
                    class="form-control form-control-sm"
                >
                  <option
                      v-for="id in (listBoxOptions[item.name] || [])"
                      :key="id"
                      :value="id"
                  >
                    {{ id }}
                  </option>
                </select>
              </div>
            </div>

            <!-- File Type -->
            <div class="form-group col-md-2 mb-3">
              <label class="col-form-label col-form-label-sm font-weight-bold">File Type</label>
              <select v-model="selectedType" class="form-control form-control-sm">
                <option
                    v-for="type in fileTypes"
                    :key="type.value"
                    :value="type.value"
                >
                  {{ type.label }}
                </option>
              </select>
            </div>

            <!-- From Date -->
            <div class="form-group col-md-3 mb-3">
              <label class="col-form-label col-form-label-sm font-weight-bold">From Date</label>
              <date-picker
                  v-model="fromDate"
                  :disabled-date="disableFutureAndToday"
                  format="YYYY-MM-DD"
                  value-type="format"
                  placeholder="Select From Date"
                  input-class="form-control form-control-sm"
              />
            </div>

            <!-- To Date -->
            <div class="form-group col-md-3 mb-3">
              <label class="col-form-label col-form-label-sm font-weight-bold">To Date</label>
              <date-picker
                  v-model="toDate"
                  :disabled-date="disableFutureAndToday"
                  format="YYYY-MM-DD"
                  value-type="format"
                  placeholder="Select To Date"
                  input-class="form-control form-control-sm"
              />
            </div>

            <!-- Choose File -->
            <div class="form-group col-md-7 mb-3">
              <label class="col-form-label col-form-label-sm font-weight-bold">Choose File</label>
              <input
                  type="file"
                  class="form-control form-control-sm"
                  ref="file"
                  :accept="selectedType"
                  @change="handleFileUpload"
              />
              <!-- Success message when file is valid -->
              <small v-if="fileValid && renamedFile" class="text-success">
                ✅ File validated successfully: {{ actualFileName }}
              </small>
              <!-- Error message when file type doesn't match -->
              <small v-else-if="fileError" class="text-danger">
                ❌ {{ fileError }}
              </small>
              <!-- Default message when no file is selected -->
              <small v-else class="text-muted">
                Allowed file type: {{ selectedType || "Select a type first" }}
              </small>
            </div>

            <!-- Upload Reason -->
            <div class="form-group col-md-4 mb-3">
              <label class="col-form-label col-form-label-sm font-weight-bold">
                Upload Reason <span class="text-danger">*</span>
              </label>

              <!-- Select dropdown for predefined reasons -->
              <select
                  v-model="selectedUploadReason"
                  @change="onUploadReasonChange"
                  class="form-control form-control-sm mb-2"
              >
                <option value="">Select a reason</option>
                <option
                    v-for="reason in uploadReasons"
                    :key="reason"
                    :value="reason"
                >
                  {{ reason }}
                </option>
              </select>

              <!-- Manual input field - only show when "Other" is selected -->
              <input
                  v-if="showManualInput"
                  type="text"
                  v-model.trim="manualUploadReason"
                  class="form-control form-control-sm"
                  placeholder="Enter reason for upload"
                  @input="validateUploadReason"
              />

              <!-- Error message -->
              <small v-if="uploadReasonError" class="text-danger">
                {{ uploadReasonError }}
              </small>
            </div>
          </div>

          <!-- Buttons aligned to right -->
          <div class="d-flex justify-content-end mt-3">
            <button
                class="btn btn-secondary btn-sm mr-2"
                @click="cancelUpload"
            >
              <i class="fas fa-times mr-1"></i> Cancel
            </button>
            <button
                class="btn btn-primary btn-sm"
                :class="{ 'disabled-btn': isUploadDisabled }"
                :disabled="isUploadDisabled"
                @click="submitFile"
            >
              <i class="fas fa-upload mr-1"></i> Upload
            </button>
          </div>

          <!-- Filename Preview -->
          <div class="row mt-3">
            <div class="col">
              <small class="font-weight-bold text-secondary">Generated Filename:</small>
              <span class="text-success font-weight-bold text-truncate">
                {{ generatedFilename }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>


<script>
import axios from 'axios'
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import {isDescendant} from "@/common/functions";

export default {
  name: 'file-upload',
  components: {
    DatePicker,
  },
  data: function () {
    return {
      file: '',
      selectedType: "", // default
      fileIdentifier: {},
      fileTypes: [],
      fromDate: null,
      toDate: null,
      renamedFile: "",
      screenletListboxDim:{},
      listBoxCache: {},
      SLlistboxDimBizName: {},
      listBoxOptions: {},
      uploadReason: "", // <-- Added this
      uploadReasonError: "",
      actualFileName:"",
      filePrivilege:{},
      fileValid: false,
      fileError: '',
      uploadReasons: [],
      selectedUploadReason: '',
      manualUploadReason: '',
      showManualInput: false,
    }
  },
  computed: {
    isUploadDisabled() {
      return !this.fromDate || !this.toDate || !this.fileIdentifier || !this.uploadReason;
    },
    generatedFilename() {
      if (!this.fileIdentifier || !this.fromDate || !this.toDate || !this.selectedType) {
        return "—";
      }

      // Collect all selected identifiers into a string
      // e.g. {dim1: "ABC", dim2: "XYZ"} → "ABC_XYZ"
      const identifiers = Object.values(this.fileIdentifier)
          .filter(Boolean) // remove empty selections
          .map(id => id.toUpperCase())
          .join("_");

      if (!identifiers) {
        return "—";
      }

      // Format dates as YYYYMMDD
      const fromDate = this.fromDate.replace(/-/g, "");
      const toDate = this.toDate.replace(/-/g, "");

      // Timestamp: YYYYMMDDHHMMSS
      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const timestamp =
          now.getFullYear().toString() +
          pad(now.getMonth() + 1) +
          pad(now.getDate()) +
          pad(now.getHours()) +
          pad(now.getMinutes()) +
          pad(now.getSeconds());

      return `FNAME_${identifiers}_FROM_${fromDate}_TO_${toDate}_EXTTS_${timestamp}_SRC_FW_${this.$store.state.userName}${this.selectedType}`;
    }
  },
  async created() {
    console.log("banner flag",this.$store.state.bannerStatus)

    let restrictedMenu = this.$store.state.user.userRoleRestriction.menuAccessInclusionArray || []
    let exclusionArray = this.$store.state.user.userRoleRestriction.menuAccessExclusionArray || [];
    const menuTree = this.$store.state.menuMasterData.filter(m => m.parentMenuId === 'Root') || []

    // Handle empty/undefined/null/["[]"] cases - all menus allowed
    const isAllMenusAllowed = !restrictedMenu ||
        restrictedMenu.length === 0 ||
        (restrictedMenu.length === 1 && restrictedMenu[0] === "[]")

    if (!isAllMenusAllowed) {
      const currentMenuId = this.$route.params.menuId
      const currentSubmenuId = this.$route.params.submenuId

      // Only check menu restrictions for routes that have these parameters
      if (currentMenuId || currentSubmenuId) {
        // Check if current IDs are in exclusion array
        const isExcluded = exclusionArray.includes(currentMenuId) ||
            exclusionArray.includes(currentSubmenuId)
        // If NEITHER menuId NOR submenuId is in the allowed list, block access
        // Check whether current IDs are directly allowed or descendants of allowed ones
        const isAllowed =
            restrictedMenu.includes(currentMenuId) ||
            restrictedMenu.includes(currentSubmenuId) ||
            isDescendant(menuTree, restrictedMenu, currentMenuId) ||
            isDescendant(menuTree, restrictedMenu, currentSubmenuId);

        if (!isAllowed || isExcluded) {
          window.alert("Invalid action, request please contact your Administrator")
          // You might want to redirect to user-restrict page here as well
          await this.$router.push('/user-restrict')
          return; // Stop execution
        }
      }
    }
    this.listenToRouter()
    this.filter = {...this.screenletListboxDefault}

  },
  watch: {
    $props: {
      async handler() {
        await this.loadListbox();
      },
      deep: true,
      immediate: true,
    },
    iframeSrc(newSrc) {
      this.iframeKey = `${newSrc}?t=${Date.now()}`;
    }
  },
  methods: {
    onUploadReasonChange() {
      this.uploadReasonError = '';

      if (this.selectedUploadReason === 'Other') {
        this.showManualInput = true;
        this.uploadReason = ''; // Clear until user types manually
      } else {
        this.showManualInput = false;
        this.manualUploadReason = ''; // Clear manual input
        this.uploadReason = this.selectedUploadReason; // Set the selected reason
        this.validateUploadReason();
      }

      console.log("upload Reaons",this.uploadReason);
    },
    disableFutureAndToday(date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date >= today; // disables today and all future dates
    },
    cancelUpload() {
      this.fromDate = null;
      this.toDate = null;
      this.uploadReason = '';
      this.selectedType = '';
      this.fileIdentifier = {};
      this.$refs.file.value = null;
      this.selectedUploadReason = "";
      this.actualFileName = "";
      this.renamedFile = "";
      this.fileValid = false;
      this.fileError = "";
    },
    updateFileType() {
      const matched = this.dataset.find(
          item => item.src_identifier_prefix === this.fileIdentifier.src_identifier_prefix
      );
      console.log(matched);
      this.selectedType = matched ? '.'+matched.src_file_type_or_database : '';
      console.log("selected Type",this.selectedType)

      // Handle the reactive object properly
      let filePrivileges = matched ? matched.file_conditions_json : {};
      console.log("filePrivileges", filePrivileges);

      // If filePrivileges has a value property (reactive object), use that
      if (filePrivileges && filePrivileges.value) {
        filePrivileges = filePrivileges.value;
      }

      // Parse the JSON string if it's a string
      if (typeof filePrivileges === 'string') {
        try {
          filePrivileges = JSON.parse(filePrivileges);
        } catch (e) {
          console.error('Error parsing file_privileges JSON:', e);
          filePrivileges = {};
        }
      }

      console.log("parsed filePrivileges", filePrivileges);

      // Get the matched privilege object
      this.filePrivilege = this.findMatchedPrivilege(filePrivileges);
      this.uploadReasons = filePrivileges?.upload_reason
      console.log("this", this.filePrivilege,this.uploadReasons);
    },
    findMatchedPrivilege(filePrivileges) {
      console.log(filePrivileges)
      if (!filePrivileges || !filePrivileges.file_privileges) {
        return {
          role: "",
          user: "",
          trust_level: "defer_manual"
        }; // Default privilege object if no privileges defined
      }

      const currentUser = this.$store.state.userName; // Replace with actual current user from login
      const currentRole = this.$store.state.roleName; // Replace with actual current role from login
      console.log("role",currentRole)

      const privileges = filePrivileges.file_privileges;

      // First check for exact user and role match (trust_level 1)
      const exactMatch = privileges.find(p =>
          p.role === currentRole && p.user === currentUser
      );
      if (exactMatch) return exactMatch;

      // Then check for role match only (trust_level 2)
      const roleMatch = privileges.find(p =>
          p.role === currentRole && p.user === ""
      );
      if (roleMatch) return roleMatch;

      // Finally check for empty role and user (trust_level 3)
      const defaultMatch = privileges.find(p =>
          p.role === "" && p.user === ""
      );
      if (defaultMatch) return defaultMatch;

      // Return default privilege object if no matches found
      return {
        role: "",
        user: "",
        trust_level: "defer_manual"
      };
    },
    listenToRouter: function () {
      console.log("listenToRouter is called")
      let $this = this
      this.$router.afterEach(function (from, to) {
        console.log(`router moved to ${from.path} from ${to.path}`)
        $this.rows = []
        $this.screenletListboxDim = {}
        $this.screenletListboxCount = null
        $this.optionValues = []
        $this.isOpenCoursel = false
        $this.messages = []
        //$this.x = {"d3l1_name":"All","d6l1_name":"All"},//need to revisit to make it dynamic
        $this.x = {}//revisited and made dynamic
        $this.y = {}
        $this.filter = {}
        $this.time3 = ''
        setTimeout(() => {
          if (from.matched.some(record => record.meta.requiresScreenConfig)) {
            console.log("inside if condition",from.matched)
            $this.loadListbox()
          }
          // $this.loadSummary($this.x)
        }, 300)

      })
    },
    fetchListboxData: async function (sqlId) {
      let res = await axios.get('/api/fetchListboxData/' + sqlId, {
        headers: {
          'info-params': JSON.stringify({
            menuId: this.$route.params.menuId,
            submenuId: this.$route.params.submenuId
          }),
          'Content-Type': 'application/json'
        }
      })
      const removeEmptyOrNull = (obj) => {
        Object.keys(obj).forEach(k =>
            (obj[k] && typeof obj[k] === 'object') && removeEmptyOrNull(obj[k]) ||
            (!obj[k] && obj[k] !== undefined) && delete obj[k]
        );
        return obj;
      };
      let restrictedArray = this.$store.state.user.userRoleRestriction.menuAccessInclusionArray || []

// Handle empty/undefined/null/["[]"] cases - all menus allowed
      const isAllMenusAllowed = !restrictedArray ||
          restrictedArray.length === 0 ||
          (restrictedArray.length === 1 && restrictedArray[0] === "[]")

      let data = this.$route.params.submenuId === "AB" ? res.data.filter(item => {
        // If all menus are allowed, return all items
        if (isAllMenusAllowed) {
          return true;
        }
        // Otherwise, only return items that are NOT in the restricted array
        return restrictedArray.includes(item.menu_id) || restrictedArray.includes(item.parent_menu_id);
      }) : res.data
      let listboxData = data
      this.dataset = removeEmptyOrNull(listboxData) //removes the null and empty values in the resultset
      console.log("filter from fetch", listboxData);
      const extractedTypes = [
        ...new Set(
            listboxData
                .map(item => item?.src_file_type_or_database)
                .filter(type => type && type.trim() !== "")
        ),
      ];

      if (extractedTypes.length > 0) {
        this.fileTypes = extractedTypes.map(type => ({
          label: type.toUpperCase(),
          value: '.'+type,
        }));
      } else {
        this.fileTypes = [
          { label: "Excel (.xlsx)", value: ".xlsx" },
          { label: "CSV (.csv)", value: ".csv" },
          { label: "PDF (.pdf)", value: ".pdf" },
          { label: "ODS (.ods)", value: ".ods" },
        ];
      }


      this.listBoxOptions = await this.makeListBoxOptions();
      console.log(this.listBoxOptions)
    },
    async makeListBoxOptions() {
      let listboxData = this.dataset
      console.clear()

      const makeOptions = (records, filterKeys, cacheKey) => {
        //make distinct values of each col
        //total cols = 4

        //cacheKey = simply, length of row + filterKeys

        const getDistinctColValues = () => {

          //for each key, we make a set to hold distinct values
          let localFilters = Object.fromEntries(
              filterKeys.map(k => [k, new Set()])
          )

          //parse through each record
          for (let row of records) {
            //for every filter-key, we add to set, this will de-dup the data
            for (let fk of filterKeys) {
              localFilters[fk].add(row[fk])
            }
          }

          //convert to a map of {k1: [distnct values for k1], k2: [distinct values for k2] .... etc}
          return Object.fromEntries(Object.entries(localFilters).map(x => {
            return [x[0], [...x[1]].sort()]
          }))
        }

        let fromCache = false; //this.listBoxCache[cacheKey]
        //let fromCache = false
        if (cacheKey && fromCache) {
          return fromCache;
        } else {
          // let ts = new Date().getTime()
          let value = getDistinctColValues();
          this.listBoxCache[cacheKey] = value;
          return value;

        }

      }

      const applyNextFilter = (currentFilter, restOfFilters, appliedFilters) => {

        let colSelected = currentFilter[0],
            colValues = currentFilter[1];

        //say 3 filters
        //salesman, brand, store
        //when nothing is selected, then distinct values of each can be used (default)
        //say one is selected, then we use that and select distinct values for rest
        //say two is selected, then use both to select distinct values for rest


        //filter records based on applied filters
        let listboxdataCopy = [...listboxData]

        for (let restFilter of restOfFilters) {
          if (restFilter[1].length > 0) {
            listboxdataCopy = listboxdataCopy.filter(x => restFilter[1].indexOf(x[restFilter[0]]) > -1);
          }
        }

        let optionsAfterRest = makeOptions(
            listboxdataCopy,
            [colSelected],
            [...Object.entries(restOfFilters), currentFilter].map(x => `${x[0]}:${x[1].join('-')}`).join()
        )

        //select records, by applying the current filter
        let recordsAfterFilter = listboxdataCopy.filter(x => colValues.indexOf(x[colSelected]) > -1);

        //from filters, remove the current filter, and previously applied filters
        let filterRemainingKeys = Object
            .keys(listboxData[0])
            .filter(x => x !== colSelected)
            .filter(x => !appliedFilters.map(y => y[0]).includes(x))

        //now we would have gotten, distinct col values for remaining filter, based on remaining data
        let options = makeOptions(
            recordsAfterFilter,
            filterRemainingKeys,
            Object.entries(this.filter).map(x => `${x[0]}:${x[1].join('-')}`).join()
        )
        return {...options, ...optionsAfterRest}
      }

      if (this.dataset.length > 0) {

        //if filter values are selected, then apply
        let initialFilterValues = makeOptions(listboxData, Object.keys(listboxData[0]), "ALL")
        let nextFilter = null;
        //iterate through each filter
        let filterEntries = Object.entries(this.fileIdentifier);
        let appliedFilters = []
        for (let currFilter of filterEntries) {

          let restOfFilters = filterEntries.filter(x => x[0] !== currFilter[0])

          //if the filter is selected
          if (currFilter[1].length > 0) {

            let prevFilter = {...(nextFilter || initialFilterValues)}
            let appliedFilter = applyNextFilter(
                currFilter,
                restOfFilters,
                appliedFilters
            )
            appliedFilters.push(currFilter)

            nextFilter = {...prevFilter, ...appliedFilter}
          }

          //else, skip this filter
        }
        return nextFilter || initialFilterValues

      } else {
        return {}

      }
    },
    loadListbox: async function () {
      let submenuParam = this.$route.params.submenuId
      console.log("submenuParma", submenuParam);
      await this.$store.dispatch("loadState")
      this.screenName = this.$store.state.menuMasterFlat.filter(m => m.menuId === submenuParam).map(m => m.menuName)[0]
      let menuMasterData = this.$store.state.menuMasterFlat.filter(m => m.menuId === submenuParam)
      console.log("menuMasterData", menuMasterData)
      let menuData = this.$store.state.fwConfigMenuScreenRender
      console.log("menuData", menuData);
      let columnMetadata = this.$store.state.colummnMetaData
      let listboxData = this.$store.state.listbox
      let currentMenuData = [];
      if (this.screenletId) {
        currentMenuData = menuData.filter(r => r.menuId === submenuParam && r.screenletInstanceId === this.screenletId)
      } else {
        currentMenuData = menuData.filter(r => r.menuId === submenuParam)
      }

      let screenletListboxSyspkArray = currentMenuData[0].screenletListboxSyspkArray ? currentMenuData[0].screenletListboxSyspkArray : []
      let currentMenuListboxData = listboxData.filter(r => screenletListboxSyspkArray.includes(r.syspk))

      function sortFuncLB(a, b) {
        return screenletListboxSyspkArray.indexOf(a.syspk) - screenletListboxSyspkArray.indexOf(b.syspk);
      }

      currentMenuListboxData.sort(sortFuncLB); //sorting the currentMenuListboxData

      this.currentMenuListboxData = currentMenuListboxData
      this.screenletListboxCount = currentMenuListboxData.length
      let currentMenuListboxSQLId = currentMenuData[0].screenletListbox1_sqlId
      this.screenletListboxType = currentMenuData[0].screenletListboxType
      if (currentMenuListboxSQLId) {
        await this.fetchListboxData(currentMenuListboxSQLId);
      }

      currentMenuListboxData.forEach((cmld, index) => {
        console.log("cmld", cmld);
        let bizNamefilterdim = columnMetadata.filter(c => c.columnName === cmld.listboxDimName);

        this.$set(this.screenletListboxDim, index, {
          name: cmld.listboxDimName,  // The key name you were using before
          isInputField: cmld.inputField  // The new input_field boolean
        });
        // Log the newly added item
        console.log('Added item:', {
          index: index,
          name: cmld.listboxDimName,
          isInputField: cmld.inputField
        });

// Log the entire array
        console.log('Current screenletListboxDim:', JSON.parse(JSON.stringify(this.screenletListboxDim)));

        this.$set(this.SLlistboxDimBizName, index, bizNamefilterdim[0]?.bizName || null);
      });
    },
    getFileExtension(filename) {
      console.log("file",filename);
      var test = filename.substring(filename.lastIndexOf('.') + 1, filename.length) || filename;
      console.log("extension", test, filename)
      // return filename.split('.').pop();
      return filename.substring(filename.lastIndexOf('.') + 1, filename.length) || filename;
    },
    validateUploadReason() {
      this.uploadReasonError = '';

      // If "Other" is selected, use the manual input
      if (this.selectedUploadReason === 'Other') {
        this.uploadReason = this.manualUploadReason;
      }

      // Validation logic
      if (!this.uploadReason) {
        this.uploadReasonError = 'Upload reason is required';
        return false;
      }

      if (this.uploadReason.length < 2) {
        this.uploadReasonError = 'Upload reason must be at least 2 characters long';
        return false;
      }

      return true;
    },
    submitFile() {
      this.validateUploadReason();
      if (this.renamedFile === '') {
        alert("Please Choose a file");
      }else if (this.uploadReasonError){
        return;
      }
      else {
        this.checkFileExist(this.renamedFile.name);
      }
    },
    resetFile() {
      this.$refs.file.value = '';
    },
    //Handles a change on the file upload
    handleFileUpload(event) {
      const file = event.target.files[0];
      console.log("file", file);

      // Reset states
      this.fileValid = false;
      this.fileError = '';
      this.renamedFile = null;
      this.actualFileName = '';

      if (!file) return;

      // Check if selectedType is set
      if (!this.selectedType) {
        this.fileError = 'Please select a file type first.';
        event.target.value = '';
        return;
      }

      // Get file extension from actual file
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      const selectedExtension = this.selectedType.toLowerCase();

      console.log("File extension:", fileExtension);
      console.log("Selected type:", selectedExtension);

      // Validate file type
      if (fileExtension !== selectedExtension) {
        this.fileError = `Allowed file type is ${this.selectedType.toUpperCase()}. You selected a ${fileExtension.toUpperCase()} file.`;
        event.target.value = '';
        return;
      }

      // If validation passes
      this.fileValid = true;
      this.renamedFile = new File([file], this.generatedFilename, { type: file.type });
      this.actualFileName = file.name;
      console.log("✅ Renamed file:", this.renamedFile.name, this.generatedFilename);
    },

    checkFileExist(fileName) {
      var self = this;
      axios.get(`/api/exists?fileName=${fileName}`)
          .then(function (resp) {
            if (resp.data.exists) {
              if (confirm("File Already Exists, Do you want to overwrite it?")) {
                self.file_load();
              } else {
                self.resetFile();
              }
            } else {
              self.file_load();
            }
          }, function () {
            alert('Error in checking file present status');
          })
    },
    file_load: function () {
      const self = this; // Store reference to Vue instance
      let formData = new FormData();
      formData.append('file', this.renamedFile);
      formData.append('actualFileName',this.actualFileName);
      formData.append('reason',this.uploadReason);
      formData.append('filePrivilege',JSON.stringify(this.filePrivilege));
      formData.append('fileIdentifier',this.fileIdentifier.src_identifier_prefix)
      let file_name = this.renamedFile.name;
      let file_extn = this.getFileExtension(file_name);
      console.log("file_extn",file_extn);
      const allowedTypes = ["xlsx", "csv", "pdf", "ods"];

      if (!allowedTypes.includes(file_extn.toLowerCase())) {
        alert('File identifier' + this.fileIdentifier.src_identifier_prefix + 'File Extension .' + file_extn + ' is not allowed. Please choose one of: ' + allowedTypes.join(", "));
        event.target.value = "";
        return;
      } else {
        axios.post('/api/upload', formData)
            .then(function () {
              console.log('SUCCESS!!');
              alert(file_name + ' Uploaded Successfully!!');

              // Use self instead of this
              self.selectedType = ".xlsx";
              self.fileIdentifier = {};
              self.fromDate = "";
              self.toDate = "";
              self.renamedFile = "";
              self.file = '';

              self.$refs.file.value = '';
              window.location.reload()
            })
            .catch(function () {
              console.log('FAILURE!!');
              alert(file_name + ' Upload Failed');
              self.$refs.file.value = '';
            });
      }
    }
  }
}
</script>
<style>
/*.v-input--selection-controls .v-input__control {
  width: 100%;
}*/
.container {
  max-width: 1200px;
}

.card {
  border-radius: 10px;
}

.form-group label {
  color: #333;
}

/* Upload button disabled style */
.disabled-btn {
  background-color: #d6d6d6 !important;
  border-color: #ccc !important;
  color: #777 !important;
  cursor: not-allowed !important;
  pointer-events: none;
}

/*button.btn {
  min-width: 100px;
}*/

.card-body {
  background-color: #fdfdfd;
}

/* Fix vertical alignment for vue2-datepicker fields */
.form-group .mx-datepicker {
  width: 100%;
}

.form-group label {
  display: block;
  margin-bottom: 4px;
  color: #333;
}

/* Align datepicker with other input fields */
.mx-input {
  height: calc(1.5em + 0.5rem + 2px);
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  line-height: 1.5;
  border-radius: 0.2rem;
  border: 1px solid #ced4da;
}

.mx-input-wrapper {
  display: block !important;
  width: 100% !important;
}

</style>